/**
 * Employer Recommendations Page
 *
 * Shows AI-powered personalized program recommendations for the employee
 */

import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Star,
  ArrowRight,
  ThumbsUp,
  ThumbsDown,
  CheckCircle,
  Clock,
  Filter,
  RefreshCw,
  Info,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import EmployerLayout from '../layout';

interface Recommendation {
  id: number;
  program_id: number;
  program_name: string;
  program_category: string;
  score: number;
  confidence: number;
  reasons: string[];
  match_factors: Record<string, any>;
  status: string;
  generated_at: string;
}

export default function EmployerRecommendationsPage() {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'viewed'>('all');

  const member = getMemberData();

  const fetchRecommendations = async (showRefreshing = false) => {
    if (!member?.id) {
      setLoading(false);
      return;
    }

    try {
      if (showRefreshing) setRefreshing(true);
      const token = getMemberToken();
      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch employee data
      const employeeUrl = getWeaveinApiUrl(`/api/employees/by-member/${member.id}`);
      const employeeResponse = await fetch(employeeUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json',
        },
      });

      if (employeeResponse.ok) {
        const empData = await employeeResponse.json();
        setEmployeeData(empData);

        // Fetch recommendations
        const recsUrl = getWeaveinApiUrl(`/api/employees/${empData.id}/recommendations`);
        const recsResponse = await fetch(recsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
            'Content-Type': 'application/json',
          },
        });

        if (recsResponse.ok) {
          const recsData = await recsResponse.json();
          setRecommendations(recsData);
        }
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  }, [member?.id]);

  const handleFeedback = async (recId: number, feedback: 'positive' | 'negative') => {
    // TODO: Send feedback to backend
    console.log('Feedback:', recId, feedback);
  };

  const handleAccept = async (recId: number) => {
    // TODO: Accept recommendation and redirect to enrollment
    console.log('Accept:', recId);
  };

  const filteredRecs = recommendations.filter((rec) => {
    if (filter === 'active') return rec.status === 'active';
    if (filter === 'viewed') return rec.status === 'viewed';
    return true;
  });

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-blue-600 bg-blue-100';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <EmployerLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-500" />
              <h1 className="text-2xl font-bold text-gray-900">Recommended For You</h1>
            </div>
            <p className="text-gray-600 mt-1">
              Personalized program recommendations based on your profile and needs
            </p>
          </div>
          <button
            onClick={() => fetchRecommendations(true)}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-500 mt-0.5" />
            <div>
              <p className="text-sm text-purple-900">
                <strong>How recommendations work:</strong> Our AI analyzes your profile, health indicators,
                and benefit usage to suggest programs that may be helpful. Match scores indicate how well
                a program fits your needs.
              </p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {(['all', 'active', 'viewed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Recommendations List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white rounded-xl h-48" />
            ))}
          </div>
        ) : filteredRecs.length > 0 ? (
          <div className="space-y-4">
            {filteredRecs.map((rec, idx) => (
              <div
                key={rec.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Rank Badge */}
                {idx < 3 && (
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-1">
                    <span className="text-white text-xs font-medium">
                      #{idx + 1} Top Recommendation
                    </span>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{rec.program_name}</h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(rec.score)}`}
                        >
                          {Math.round(rec.score * 100)}% match
                        </span>
                      </div>

                      <p className="text-sm text-gray-500 mb-3">
                        {rec.program_category?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </p>

                      {/* Reasons */}
                      <div className="space-y-2 mb-4">
                        <p className="text-sm font-medium text-gray-700">Why we recommend this:</p>
                        <ul className="space-y-1">
                          {(rec.reasons || ['Based on your profile and benefit preferences']).map((reason, i) => (
                            <li key={i} className="flex items-start text-sm text-gray-600">
                              <Star className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Confidence indicator */}
                      {rec.confidence && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>Confidence:</span>
                          <div className="flex-1 max-w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${rec.confidence * 100}%` }}
                            />
                          </div>
                          <span>{Math.round(rec.confidence * 100)}%</span>
                        </div>
                      )}
                    </div>

                    {/* Match Score Visualization */}
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-2xl font-bold text-purple-600">
                            {Math.round(rec.score * 100)}
                          </span>
                          <span className="text-xs text-purple-500 block">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Was this helpful?</span>
                      <button
                        onClick={() => handleFeedback(rec.id, 'positive')}
                        className="p-2 rounded hover:bg-green-50 text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleFeedback(rec.id, 'negative')}
                        className="p-2 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleAccept(rec.id)}
                      className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                    >
                      Learn More <ArrowRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Recommendations Yet</h3>
            <p className="text-gray-500 mb-4">
              We're analyzing your profile to find the best program matches for you.
              Check back soon!
            </p>
            <button
              onClick={() => fetchRecommendations(true)}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Refresh Recommendations
            </button>
          </div>
        )}
      </div>
    </EmployerLayout>
  );
}
